import { useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const LEVELS = {
  high: { color: "#5B8A6F", label: "High", heading: "Rowan is confident" },
  medium: { color: "#E8A838", label: "Medium", heading: "Rowan made an inference" },
  low: { color: "#D96C4A", label: "Low", heading: "Rowan made a best guess" },
};

export function ConfidenceIndicator({ confidence, hideLabel = false, testId = "confidence-indicator" }) {
  const lvl = LEVELS[confidence];
  if (!lvl) return null;
  return (
    <span className="inline-flex items-center gap-1.5" aria-label={`${lvl.label} confidence`} data-testid={testId}>
      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: lvl.color }} />
      {!hideLabel && <span className="text-[11px] font-semibold" style={{ color: lvl.color }}>{lvl.label}</span>}
    </span>
  );
}

function TooltipBody({ lvl, displayReason, corrected, category, categoryOptions, onSelectCategory, onDone, testId, fullReason = false }) {
  const [picking, setPicking] = useState(false);
  return (
    <>
      <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }} data-testid={`${testId}-heading`}>
        {lvl.heading}
      </p>
      <TruncatedReason reason={displayReason} testId={testId} full={fullReason} />
      {corrected && (
        <p className="text-xs mt-2 font-normal" style={{ color: "var(--bk-text-2)", opacity: 0.85 }} data-testid={`${testId}-corrected-notice`}>
          You changed this to {category}.
        </p>
      )}
      {onSelectCategory && (
        picking ? (
          <select
            autoFocus
            defaultValue={category || ""}
            onChange={(e) => { onSelectCategory(e.target.value); setPicking(false); onDone(); }}
            data-testid={`${testId}-category-picker`}
            className="mt-3 w-full rounded-md border px-2 py-1.5 text-sm bg-white outline-none"
            style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}
          >
            <option value="" disabled>Pick a category…</option>
            {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        ) : (
          <button
            type="button"
            onClick={() => setPicking(true)}
            data-testid={`${testId}-change-category-link`}
            className="mt-3 text-sm font-semibold hover:underline focus:outline-none focus-visible:ring-2 rounded"
            style={{ color: "var(--bk-accent)" }}
          >
            Change category
          </button>
        )
      )}
    </>
  );
}

export function ConfidenceBadge({
  confidence, reason, category, originalCategory,
  categoryOptions = [], onSelectCategory = null,
  hideLabel = false, allowBottomSheet = false, testId = "confidence-badge",
}) {
  const [open, setOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const triggerRef = useRef(null);
  const lvl = LEVELS[confidence];
  if (!lvl) return null;
  const displayReason = (reason || "").trim() || "Rowan categorized this transaction automatically.";
  const corrected = originalCategory && category && category !== originalCategory;

  const shouldUseSheet = () => {
    if (!allowBottomSheet || typeof window === "undefined" || window.innerWidth >= 768) return false;
    const rect = triggerRef.current?.getBoundingClientRect();
    return !!rect && rect.top > window.innerHeight / 2;
  };

  const bodyProps = { lvl, displayReason, corrected, category, categoryOptions, onSelectCategory, testId };

  return (
    <>
      <Popover
        open={open}
        onOpenChange={(v) => {
          if (v && shouldUseSheet()) { setSheetOpen(true); return; }
          setOpen(v);
        }}
      >
        <PopoverTrigger asChild>
          <button
            ref={triggerRef}
            type="button"
            aria-label={`${lvl.label} confidence`}
            data-testid={testId}
            className="bk-conf-trigger inline-flex items-center gap-1.5 rounded-full px-2 py-1 border transition-colors hover:bg-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 min-w-[28px] min-h-[28px] max-md:min-w-[44px] max-md:min-h-[44px] justify-center"
            style={{ borderColor: `${lvl.color}44` }}
          >
            <span className="bk-conf-dot w-2.5 h-2.5 rounded-full shrink-0" style={{ background: lvl.color }} />
            {!hideLabel && <span className="bk-conf-label text-[11px] font-semibold" style={{ color: lvl.color }}>{lvl.label}</span>}
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[280px] p-4 rounded-2xl shadow-lg border bg-white data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0"
          style={{ borderColor: "var(--bk-border)", animationDuration: "100ms" }}
          sideOffset={6}
          collisionPadding={16}
          data-testid={`${testId}-tooltip`}
        >
          <TooltipBody {...bodyProps} onDone={() => setOpen(false)} />
        </PopoverContent>
      </Popover>

      {allowBottomSheet && (
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetContent side="bottom" className="rounded-t-2xl px-5 pb-8" data-testid={`${testId}-bottom-sheet`}>
            <div className="mx-auto w-10 h-1.5 rounded-full bg-black/15 mb-2" aria-hidden="true" />
            <SheetHeader className="text-left">
              <div className="flex items-center justify-between">
                <SheetTitle className="text-base" style={{ color: "var(--bk-text)" }}>
                  <ConfidenceIndicator confidence={confidence} testId={`${testId}-sheet-indicator`} />
                </SheetTitle>
                <button type="button" onClick={() => setSheetOpen(false)} data-testid={`${testId}-sheet-done`} className="text-sm font-semibold px-3 py-1.5 rounded-full" style={{ color: "var(--bk-accent)" }}>
                  Done
                </button>
              </div>
            </SheetHeader>
            <div className="mt-1">
              <TooltipBody {...bodyProps} onDone={() => setSheetOpen(false)} fullReason />
            </div>
          </SheetContent>
        </Sheet>
      )}
    </>
  );
}

function TruncatedReason({ reason, testId, full = false }) {
  const [expanded, setExpanded] = useState(false);
  const capped = reason.length > 200 ? `${reason.slice(0, 200)}...` : reason;
  if (full) {
    return <p className="text-sm mt-1.5" style={{ color: "var(--bk-text-2)" }} data-testid={`${testId}-reason`}>{capped}</p>;
  }
  const needsTruncate = capped.length > 80 && !expanded;
  return (
    <p className="text-sm mt-1.5" style={{ color: "var(--bk-text-2)" }} data-testid={`${testId}-reason`}>
      {needsTruncate ? `${capped.slice(0, 80)}…` : capped}
      {needsTruncate && (
        <button type="button" onClick={() => setExpanded(true)} className="ml-1 text-xs font-semibold hover:underline" style={{ color: "var(--bk-accent)" }} data-testid={`${testId}-reason-more`}>
          more
        </button>
      )}
    </p>
  );
}
